import { Component, OnInit, OnDestroy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { VideoPlaylist } from '@hsi/video-playlist';
import { environment } from '../../../environments/environment';
import { AppSharedService } from '../../services/app-shared.service';
import { AppSettingsService } from '../../services/app-settings.service';
import { AppStoreService } from '../../services/app-store.service';
import { AppUtilityService } from '../../services/app-utility.service';
import { CommonModule } from '@angular/common';
import { Subscription } from 'rxjs';

@Component({
    selector: 'app-playlist',
    templateUrl: './playlist.component.html',
    styleUrls: ['./playlist.component.scss'],
    imports: [CommonModule]
})
export class PlaylistComponent implements OnInit, OnDestroy {
  controller!: VideoPlaylist.Core.Services.PlaylistController;
  playlists: VideoPlaylist.Core.Interfaces.Playlist[] = [];
  selectedPlaylist = 0;
  ready = false;
  playState = false;
  displayCaptions = false;
  navOpen = false;
  isMenuOpen = false;
  showInfo = false;
  displayLessonUnits = false;
  lessonUnitTransition = false;
  autoPlay = false;
  captionLanguage = 'en';
  videoQuality = 'md';
  fullScreenVideo = false;
  offlineMode = false;
  transitionTimeout: any;
  downloadHd = false;
  tennant = localStorage.getItem('company') || 'ashi';
  
  private playlistService!: VideoPlaylist.Core.Services.PlaylistService;
  private dataSynchronizationService!: VideoPlaylist.Core.Services.DataSynchronizationService;
  private subscriptions = new Subscription();

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private appShared: AppSharedService,
    private appStore: AppStoreService,
    private appSettings: AppSettingsService,
    private appUtility: AppUtilityService
  ) {
    const authenticatedUser = this.appStore.authenticationService.authenticatedUser;
    if (!authenticatedUser) {
      this.router.navigate(['/']);
      return;
    }
  }

  ngOnInit(): void {
    this.autoPlay = localStorage.getItem('autoPlay') === 'true';
    this.fullScreenVideo = localStorage.getItem('fullScreenVideo') === 'true';
    this.videoQuality = localStorage.getItem('videoQuality') || 'md';
    this.displayCaptions = localStorage.getItem('displayCaptions') === 'true';
    this.captionLanguage = localStorage.getItem('captionLanguage') || 'en';
    this.downloadHd = this.appSettings.downloadHd();

    const trainingResourceMicroservice = new VideoPlaylist.Infrastructure.Repositories.TrainingResourceMicroservice(
      this.appStore.microserviceHttpClient,
      environment.microservice.trainingResources
    );
    const videoPlaylistClientRepository = new VideoPlaylist.Infrastructure.Repositories.VideoPlaylistIndexedDbClientRepository();
    this.playlistService = new VideoPlaylist.Core.Services.PlaylistService(
      trainingResourceMicroservice,
      videoPlaylistClientRepository
    );
    this.dataSynchronizationService = new VideoPlaylist.Core.Services.DataSynchronizationService(videoPlaylistClientRepository);

    window.addEventListener('keydown', this.setKeyBindings.bind(this));

    if (this.appShared.playlistStore.value.length) {
      this.initializePlaylistController(this.appShared.playlistStore.value);
    } else {
      this.playlistService.get().then(playlists => this.initializePlaylistController(playlists));
    }

    // Subscribe to settings changes
    this.subscriptions.add(this.appSettings.offlineMode$.subscribe(value => {
      this.offlineMode = value;
      this.setVideoQuality();
    }));
    this.subscriptions.add(this.appSettings.autoPlay$.subscribe(value => {
      this.autoPlay = value;
      if (this.autoPlay) this.play();
    }));
    this.subscriptions.add(this.appSettings.fullScreenVideo$.subscribe(value => {
      this.fullScreenVideo = value;
      if (this.fullScreenVideo && this.playState) this.setVideoFullScreenState();
    }));
    this.subscriptions.add(this.appSettings.videoQuality$.subscribe(value => {
      this.videoQuality = value;
      this.setVideoQuality();
    }));
    this.subscriptions.add(this.appSettings.displayCaptions$.subscribe(value => {
      this.displayCaptions = value;
      this.setClosedCaptions();
    }));
    this.subscriptions.add(this.appSettings.captionLanguage$.subscribe(value => {
      this.captionLanguage = value;
      this.setClosedCaptions();
    }));
  }

  ngOnDestroy(): void {
    window.removeEventListener('keydown', this.setKeyBindings.bind(this));
    if (this.transitionTimeout) clearTimeout(this.transitionTimeout);
    this.subscriptions.unsubscribe();
  }

  initializePlaylistController(playlists: VideoPlaylist.Core.Interfaces.Playlist[]): void {
    if (!this.appShared.playlistStore.value.length) {
      this.appShared.playlistStore.next(playlists);
    }
    this.playlists = playlists;
    this.selectedPlaylist = this.playlists.findIndex(
      x => x.customerPlaylistGuid === this.route.snapshot.paramMap.get('id')
    );
    if (this.selectedPlaylist === -1) {
      this.appUtility.displayMessage({
        text: "Playlist not found.",
        actionText: "OK",
        duration: 4000
      });

      this.router.navigate(['/', this.tennant, 'directory']);
      return;
    }
    this.setPlaylist(this.selectedPlaylist);
  }

  async setPlaylist(index: number): Promise<void> {
    const controller = new VideoPlaylist.Core.Services.PlaylistController(
      this.playlists[index],
      this.dataSynchronizationService,
      this.playlistService,
      this.appUtility.resolveVideoQuality()
    );
    await controller.synchronizeWithClient();
    for (let i = 0; i < controller.lessonUnits.length; i++) {
      const localUrl = await this.playlistService.getLocalTrainingElementThumbnail(
        controller.lessonUnits[i].primaryAsset.id,
        controller.playlist.customerPlaylistId
      );
      if (localUrl) controller.lessonUnits[i].lessonUnitThumbnailUrl = localUrl;
    }
    this.controller = controller;
    this.appShared.setTitle(this.controller.playlist.title);
    this.ready = true;
  }

  play(): void {
    const videoPlayer = document.getElementById('videoPlayer') as HTMLVideoElement;
    videoPlayer?.play();
    this.setClosedCaptions();
    this.setVideoFullScreenState();
  }

  onVideoEnd(): void {
    if (this.autoPlay) this.nextLessonUnit();
  }

  togglePlay(): void {
    const videoPlayer = document.getElementById('videoPlayer') as HTMLVideoElement;
    if (videoPlayer) {
      if (videoPlayer.paused) videoPlayer.play();
      else videoPlayer.pause();
    }
  }

  nextLessonUnit(): void {
    this.controller.nextLessonUnit();
    this.postNavigation();
  }

  nextLessonUnitAsset(): void {
    this.controller.nextLessonUnitAsset();
    this.postNavigation();
  }

  previousLessonUnit(): void {
    this.controller.previousLessonUnit();
    this.postNavigation();
  }

  previousLessonUnitAsset(): void {
    this.controller.previousLessonUnitAsset();
    this.postNavigation();
  }

  postNavigation(): void {
    this.lessonUnitTransition = true;
    this.scrollToSelected();
    if (!this.autoPlay) this.playState = false;
    this.setClosedCaptions();
    if (this.transitionTimeout) clearTimeout(this.transitionTimeout);
    this.transitionTimeout = setTimeout(() => (this.lessonUnitTransition = false), 2000);
  }

  scrollToSelected(): void {
    const el = document.getElementById(this.controller.lessonUnit.id.toString());
    el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }

  deferredScrollToSelected(): void {
    setTimeout(() => this.scrollToSelected(), 0);
  }

  displayJumpToSection(): boolean {
    return this.controller.playlist.sections.some(x => x.title);
  }

  async getAssetUrl(): Promise<any> {
    return await this.playlistService.getLocalTrainingElementAssets(
      this.controller.trainingElement.id,
      this.controller.playlist.customerPlaylistId
    );
  }

  getVideoUrl(): string {
    let videoAsset = this.resolveAssetByType('MdMp4VideoFile');
    if (this.videoQuality === 'hd') videoAsset = this.resolveAssetByType('HdMp4VideoFile');
    if (this.videoQuality === 'sd' || (this.offlineMode && !this.downloadHd)) videoAsset = this.resolveAssetByType('Mp4VideoFile');
    if (!videoAsset) return '';
    return this.parseSsl(videoAsset.url);
  }

  resolveAssetByType(type: string): any {
    return this.controller.trainingElement.data.assets.find((x: any) => x.type === type);
  }

  parseSsl(url: string): string {
    return url
      .replace('http://embed.wistia.com', 'https://embed-ssl.wistia.com')
      .replace('https://embed.wistia.com', 'https://embed-ssl.wistia.com');
  }

  setKeyBindings(e: KeyboardEvent): void {
    switch (e.keyCode) {
      case 33: // page up
      case 40: // down arrow
        this.nextLessonUnit();
        e.preventDefault();
        break;
      case 39: // arrow right
        this.nextLessonUnitAsset();
        e.preventDefault();
        break;
      case 37: // arrow left
        this.previousLessonUnitAsset();
        e.preventDefault();
        break;
      case 34: // page down
      case 38: // arrow up
        this.previousLessonUnit();
        e.preventDefault();
        break;
      case 32: // space
      case 116: // F5
      case 27: // escape
        this.togglePlay();
        e.preventDefault();
        break;
      case 18: // alt
      case 190: // period
        this.displayLessonUnits = !this.displayLessonUnits;
        e.preventDefault();
        break;
    }
  }

  setVideoFullScreenState(): void {
    if (!this.fullScreenVideo) return;
    const videoPlayer = document.getElementById('videoPlayer') as HTMLVideoElement;
    if (videoPlayer?.requestFullscreen) videoPlayer.requestFullscreen();
    else if ((videoPlayer as any).msRequestFullscreen) (videoPlayer as any).msRequestFullscreen();
    else if ((videoPlayer as any).mozRequestFullScreen) (videoPlayer as any).mozRequestFullScreen();
  }

  setClosedCaptions(): void {
    const videoPlayer = document.getElementById('videoPlayer') as HTMLVideoElement;
    if (videoPlayer) {
      if (videoPlayer.textTracks.length > 0) videoPlayer.textTracks[0].mode = 'hidden'; // en
      if (videoPlayer.textTracks.length > 1) videoPlayer.textTracks[1].mode = 'hidden'; // es
      if (this.displayCaptions && this.captionLanguage === 'en')
        videoPlayer.textTracks[0].mode = 'showing';
      if (this.displayCaptions && this.captionLanguage === 'es')
        videoPlayer.textTracks[1].mode = 'showing';
    }
  }

  setVideoQuality(): void {
    const videoPlayer = document.getElementById('videoPlayer') as HTMLVideoElement;
    if (videoPlayer) {
      const currentTime = videoPlayer.currentTime;
      const trainingElement = document.getElementById('training-element')?.children[0] as any;
      if (trainingElement) {
        trainingElement.src = this.getVideoUrl();
        trainingElement.currentTime = currentTime;
      }
    }
  }
}
